/**
 * SubscriptionAlert Component
 * Displays subscription-related alerts (expiring, expired, payment failed, etc.)
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle, Clock, CreditCard, Zap, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useSubscriptionStore } from '@/stores/subscription-store';
import type { AlertType } from '@/types/subscription';

const alertConfig: Record<AlertType, {
  icon: typeof AlertTriangle;
  variant: 'warning' | 'destructive' | 'info';
}> = {
  expiring: { icon: Clock, variant: 'warning' },
  expired: { icon: AlertCircle, variant: 'destructive' },
  payment_failed: { icon: CreditCard, variant: 'destructive' },
  trial_ending: { icon: Zap, variant: 'info' },
  grace_period: { icon: AlertTriangle, variant: 'warning' },
};

interface SubscriptionAlertProps {
  /** Whether to show dismiss button */
  dismissible?: boolean;
  /** Show only the first alert */
  single?: boolean;
  /** Custom className */
  className?: string;
}

export function SubscriptionAlert({
  dismissible = false,
  single = true,
  className,
}: SubscriptionAlertProps) {
  const { t } = useTranslation();
  const alerts = useSubscriptionStore((state) => state.alerts);
  const clearAlerts = useSubscriptionStore((state) => state.clearAlerts);

  if (alerts.length === 0) return null;

  const alertsToShow = single ? [alerts[0]] : alerts;

  return (
    <div className={className}>
      {alertsToShow.map((alert, index) => {
        const config = alertConfig[alert.type];
        const Icon = config.icon;

        return (
          <Alert key={`${alert.type}-${index}`} variant={config.variant} className="mb-2">
            <Icon className="h-4 w-4" />
            <AlertTitle className="flex items-center justify-between">
              <span>{t(`billing.alerts.${alert.type}.title`, alert.message)}</span>
              {dismissible && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 -mr-2"
                  onClick={clearAlerts}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </AlertTitle>
            <AlertDescription className="flex items-center justify-between mt-2">
              <span>
                {alert.daysRemaining !== undefined && (
                  <span className="font-medium">
                    {t('billing.alerts.daysRemaining', { count: alert.daysRemaining })}
                  </span>
                )}
              </span>
              {alert.actionUrl && (
                <Button asChild variant="outline" size="sm">
                  <Link to={alert.actionUrl}>
                    {alert.actionLabel || t('billing.alerts.action')}
                  </Link>
                </Button>
              )}
            </AlertDescription>
          </Alert>
        );
      })}
    </div>
  );
}
